import { useMutation } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import axios from 'axios'
import Lottie from 'lottie-react'
import styled from 'styled-components'

import { MODALS_ID } from '../../../constants'
import type { components } from '../../../constants/schema'

import { toast } from '../../../utils'
import { abrSuccessSubscriptionProduct } from '../../../utils/abr'
import { chSuccessSubscriptionProduct } from '../../../utils/ch'

import { ModalContext } from '../../../provider/ModalProvider'

import { useUserInfo } from '../../../hooks/useUserInfo'

import { purchaseProduct, getProductDetail } from '../../../api/product'

import { ModalWrap } from '../../../components/modal/ModalWrap'
import { Content } from '../../../components/container/Content'
import { Text } from '../../../components/text'
import { OrderSuccessModal } from './OrderSuccessModal'
import { OrderFailModal } from './OrderFailModal'

import orderLottie from '../../../assets/lottie/order.json'

interface Props {
  saleData: {
    id: number
    count: components['schemas']['ReqQueryProductPurchase']['count']
    terms: components['schemas']['ReqQueryProductPurchase']['terms']
  }
  success: () => void
  onDismiss: () => void
}

const ERROR_BLACK_LIST = ['API_000404', 'API_044400', 'UTIL_001004', 'UTIL_001002', 'UTIL_001003']

export function OrderLoadingModal({ saleData, success, onDismiss }: Props) {
  const { openModal, closeModal } = useContext(ModalContext)

  const { data: userInfo } = useUserInfo()

  const { mutate } = useMutation({
    mutationFn: purchaseProduct,
    onSuccess: async (response) => {
      const productDetail = await getProductDetail(saleData.id)

      const identification = userInfo?.identification
      const product = productDetail ?? undefined

      abrSuccessSubscriptionProduct(product, response)
      chSuccessSubscriptionProduct(identification, product, response)

      openOrderSuccessModal(response)
      onDismiss()
    },
    onError: (error) => {
      const defaultErrorMessage = '청약에 실패했어요.'
      if (axios.isAxiosError(error)) {
        const filteredErrorList = ERROR_BLACK_LIST.includes(error.response?.data.CODE)
        const errorMessage = filteredErrorList ? error.response?.data.message : defaultErrorMessage
        onDismiss()
        openOrderFailModal()
        toast(errorMessage, error, true)
        return
      }
      openOrderFailModal()
      toast(defaultErrorMessage, error, true)
      onDismiss()
    },
  })

  useEffect(() => {
    purchases()
  }, [])

  const openOrderSuccessModal = (orderData: components['schemas']['UserProductDto']) => {
    openModal({
      id: MODALS_ID.ORDER_SUCCESS,
      Component: () => (
        <OrderSuccessModal
          orderData={orderData}
          onDismiss={() => {
            success()
            onDismiss()
            closeModal(MODALS_ID.ORDER_SUCCESS)
          }}
        />
      ),
    })
  }

  const openOrderFailModal = () => {
    openModal({
      id: MODALS_ID.ORDER_FAIL,
      Component: () => (
        <OrderFailModal
          onDismiss={() => {
            success()
            onDismiss()
            closeModal(MODALS_ID.ORDER_FAIL)
          }}
        />
      ),
    })
  }

  const purchases = async () => {
    const { id, count, terms } = saleData

    mutate({ productId: id, data: { count, terms } })
  }

  return (
    <ModalWrap>
      <Content>
        <Container>
          <Lottie animationData={orderLottie} className="lottie" />
          <Text size={24} weight={600} lineHeight={34} block={true} $textAlign="center">
            결제가 진행 중이에요.
          </Text>
          <Text size={16} color="text_secondary" block={true} $textAlign="center">
            잠시만 기다려주세요.
          </Text>
        </Container>
      </Content>
    </ModalWrap>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100%;
  .lottie {
    width: calc(100% - 96px);
    margin-top: calc(100);
    margin-bottom: -10%;
  }
`
